import React from 'react';
import { DraggableAttributes } from '@dnd-kit/core';
import { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';
import { useLocation, useNavigate } from 'react-router-dom';
import { Wand2, Loader2, FileText, Image, Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Task } from '@/types/tasks';
import MoveCardButton from '@/components/ui/MoveCardButton';
import { Checkbox } from '@/components/ui/checkbox';
import ContainerButton from '@/components/ui/ContainerButton';
import { detectContentTypes, detectContentTypesWithGenerated, getContentTypeLabel, type ContentType } from '@/utils/task-content-detection';
import TaskCardTitle from './TaskCardTitle';
import TaskCardDescription from './TaskCardDescription';
import TaskCardBadges from './TaskCardBadges';
import TaskCardDates from './TaskCardDates';
import { useIsMobile } from '@/hooks/layout/use-mobile';

// Function to detect if a task needs content generation using our unified detection system
const isContentGenerationTask = (task: Task): boolean => {
  const contentDetection = detectContentTypes(task.title, task.description || '');
  return contentDetection.detectedTypes.length > 0;
};

interface TaskCardContentProps {
  task: Task;
  isCompleted: boolean;
  handleCheckboxChange: (checked: boolean) => void;
  handleClickStopPropagation: (e: React.MouseEvent) => void;
  onCardClick?: (e: React.MouseEvent) => void;
  dragHandleRef?: (element: HTMLElement | null) => void;
  dragListeners?: SyntheticListenerMap;
  dragAttributes?: DraggableAttributes;
  hideGoalName?: boolean;
  // Function to open page creation modal with task data
  onGenerateContent?: (taskTitle: string, taskDescription: string, taskId: string) => void;
  // Function to open research creation modal with task data
  onGenerateResearch?: (taskTitle: string, taskDescription: string, taskId: string) => void;
  // Function to open image creation modal with task data
  onGenerateImage?: (taskTitle: string, taskDescription: string, taskId: string) => void;
  // Loading state for content generation
  isGeneratingContent?: boolean;
  workbookId?: string; // For navigation to generated content
}

const TaskCardContent = ({
  task,
  isCompleted,
  handleCheckboxChange,
  handleClickStopPropagation,
  onCardClick,
  dragHandleRef,
  dragListeners,
  dragAttributes,
  hideGoalName = false,
  onGenerateContent,
  onGenerateResearch,
  onGenerateImage,
  isGeneratingContent,
  workbookId,
}: TaskCardContentProps) => {
  const isMobile = useIsMobile();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Check if we're on the Manage Tasks page (Project Tasks page)
  const isOnManageTasksPage = location.pathname.includes('/project/');
  
  // Use enhanced content detection that checks for existing generated content
  const contentDetection = detectContentTypesWithGenerated(task);
  
  // Show buttons only on Manage Tasks page for non-completed tasks
  const shouldShowButtons = isOnManageTasksPage && 
                                   !isCompleted && 
                           (contentDetection.shouldShowGenerateButtons || contentDetection.shouldShowViewButtons);
  
  // Format the created date if it exists, otherwise use a fallback
  const createdDate = task.createdAt 
    ? new Date(task.createdAt) 
    : new Date(Date.now() - Math.floor(Math.random() * 30) * 24 * 60 * 60 * 1000);

  // Handle content generation based on type (for Generate buttons)
  const handleGenerateContent = (type: ContentType) => {
    const title = task.title;
    const description = task.description || '';

    if (type === 'research') {
      onGenerateResearch?.(title, description, task.id);
    } else if (type === 'image') {
      onGenerateImage?.(title, description, task.id);
    } else {
      // Default to document generation for 'document' type and fallback
      onGenerateContent?.(title, description, task.id);
    }
  };

  // Handle viewing existing content (for View buttons)
  const handleViewContent = (type: ContentType, contentId: string) => {
    if (!workbookId) {
      console.warn('[TaskCardContent] Cannot navigate: workbookId not provided');
      return;
    }
    
    switch (type) {
      case 'document':
        navigate(`/project/${workbookId}/page/${contentId}`);
        break;
      case 'image':
        navigate(`/project/${workbookId}/images?openImage=${contentId}`);
        break;
      case 'research':
        navigate(`/project/${workbookId}/resources?openResearch=${contentId}`);
        break;
    }
  };

  const getIconForContentType = (type: ContentType) => {
    switch (type) {
      case 'document':
        return FileText;
      case 'image':
        return Image;
      case 'research':
        return Search;
      default:
        return FileText;
    }
  };

  return (
    <div className="group relative">
      {/* Checkbox Overlay - positioned to align with text center */}
      <div 
        className="absolute z-20 flex-shrink-0"
        style={{
          top: isMobile ? '16px' : '24px', // Aligns checkbox center with text center (adjusted -1px)
          left: isMobile ? '12px' : '20px', // Matches content padding
        }}
        onClick={(e) => {
          e.stopPropagation();
          handleClickStopPropagation(e);
        }}
      >
        <Checkbox
          checked={isCompleted}
          onCheckedChange={handleCheckboxChange}
          className="transition-all duration-200 group-hover:scale-110 group-hover:ring-2 group-hover:ring-blue-400/50 group-hover:ring-offset-1 bg-white/90 dark:bg-gray-800/90"
        />
      </div>

      <div
        className="relative rounded-lg overflow-hidden h-full flex flex-col cursor-pointer transition-all duration-300 ease-in-out shadow-sm hover:shadow-md hover:shadow-amber-100/30 dark:hover:shadow-amber-900/20 hover:scale-[1.02] hover:translate-y-[-2px] border-2 border-brandBlue/30 dark:border-brandPurple/30 bg-gradient-to-br from-brandBlue/10 to-brandPurple/10"
        onClick={onCardClick}
        style={{ 
          borderRadius: '0.5rem',
          transform: 'translateZ(0)',
          backfaceVisibility: 'hidden',
          willChange: 'transform, box-shadow',
          transformStyle: 'preserve-3d',
          WebkitFontSmoothing: 'antialiased'
        }}
      >
      {/* Beautiful glass morphism background - ensuring exact same border radius */}
      <div 
        className="absolute inset-0 bg-gradient-to-br from-white/40 via-white/20 to-white/5 dark:from-black/60 dark:to-black/30 backdrop-blur-xl backdrop-saturate-150 -z-10 will-change-transform transition-all duration-700 ease-in-out group-hover:from-white/70 group-hover:via-white/40 group-hover:to-white/20 dark:group-hover:from-black/40 dark:group-hover:to-black/10" 
        style={{ 
          transform: 'translateZ(0)',
          borderRadius: 'inherit' // Inherit the exact border radius from parent
        }}
      />

      {/* Move button in top right corner */}
      {dragHandleRef && (
        <div className="absolute top-3 right-3 z-10">
          <div
            ref={dragHandleRef}
            {...dragListeners}
            {...dragAttributes}
            className="cursor-grab active:cursor-grabbing touch-none select-none"
            style={{ 
              // Ensure hover events pass through to button
              pointerEvents: 'auto'
            }}
          >
            <MoveCardButton
              variant="glass"
              size="sm"
              iconType="vertical"
              className="opacity-70 hover:opacity-100"
              onClick={(e) => {
                e.stopPropagation();
                // Drag functionality is handled by the drag handle wrapper
              }}
            />
          </div>
        </div>
      )}

      {/* Task Content with proper padding - ensuring exact same border radius */}
      <div 
        className={`${isMobile ? 'p-3 pb-4' : 'p-5 pb-6'} flex flex-col h-full`} 
        style={{ 
          willChange: 'contents',
          borderRadius: 'inherit' // Inherit the exact border radius from parent
        }}
      >
        <div className={cn(
          "flex items-start gap-3 w-full h-full min-w-0",
          "pr-8", // Right padding to avoid overlap with move button
          { "opacity-70": isCompleted }
        )}>
          <div className="flex-1 min-w-0 overflow-hidden">
            {/* Top row with checkbox and title */}
            <div className="flex items-center mb-2">
              <TaskCardTitle 
                title={task.title}
                isCompleted={isCompleted}
                onCheckboxChange={handleCheckboxChange}
                handleClickStopPropagation={handleClickStopPropagation}
              />
            </div>
            
            <TaskCardDescription 
              description={task.description} 
              isCompleted={isCompleted} 
            />
            
            <div className="flex items-center justify-between mt-1 gap-2 min-w-0">
              {/* Due date on the left */}
              <TaskCardDates 
                dueDate={task.dueDate}
                createdDate={createdDate}
                isCompleted={isCompleted}
                goalId={task.goalId}
                hideGoalName={hideGoalName}
                taskId={task.id}
              />
              
              {/* Generate/View Content buttons in the middle (only on Manage Tasks page for content tasks) */}
              {shouldShowButtons && (
                <div 
                  className="flex items-center gap-1 sm:gap-2 flex-shrink-0"
                  onClick={handleClickStopPropagation}
                >
                  {contentDetection.shouldShowGenerateButtons && contentDetection.detectedTypes.map((type) => {
                    const IconComponent = getIconForContentType(type);
                    return (
                  <ContainerButton
                        key={type}
                        onClick={() => handleGenerateContent(type)}
                    icon={isGeneratingContent ? 
                      <Loader2 size={14} className="animate-spin" /> : 
                          <IconComponent size={14} />
                    }
                    disabled={isGeneratingContent}
                    className={cn(
                      "h-6 sm:h-7 px-1 sm:px-2 text-xs transition-all duration-200 min-w-0",
                      isGeneratingContent && "opacity-70 cursor-not-allowed"
                    )}
                  >
                        {isGeneratingContent ? "Loading..." : getContentTypeLabel(type)}
                      </ContainerButton>
                    );
                  })}
                  
                  {contentDetection.shouldShowViewButtons && contentDetection.detectedTypes.map((type) => {
                    const IconComponent = getIconForContentType(type);
                    const contentId = type === 'document' ? task.generated_document_id :
                                    type === 'image' ? task.generated_image_id :
                                    task.generated_research_id;
                    
                    if (!contentId) return null; // Safety check
                    
                    return (
                      <ContainerButton
                        key={type}
                        onClick={() => handleViewContent(type, contentId)}
                        icon={<IconComponent size={14} />}
                        className={cn(
                          "h-6 sm:h-7 px-1 sm:px-2 text-xs transition-all duration-200 text-white min-w-0",
                          type === 'document' && "bg-purple-500 hover:bg-purple-600",
                          type === 'image' && "bg-blue-500 hover:bg-blue-600",
                          type === 'research' && "bg-green-500 hover:bg-green-600"
                        )}
                      >
                        <span className="hidden sm:inline">View </span>{type === 'document' ? 'Doc' : type === 'image' ? 'Img' : 'Research'}
                  </ContainerButton>
                    );
                  })}
                </div>
              )}
              
              {/* Importance and Priority badges on the right */}
              <div className="flex items-center gap-2">
                <TaskCardBadges task={task} isCompleted={isCompleted} />
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default TaskCardContent;
